import type { ReactNode } from "react";
import Box from "@mui/material/Box";
import IconButton from "@mui/material/IconButton";
import LinearProgress from "@mui/material/LinearProgress";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import DeleteOutlinedIcon from "@mui/icons-material/DeleteOutlined";
import ReplayIcon from "@mui/icons-material/Replay";
import type { ReportUploadedFile } from "@/api/report/_models/ReportUpload";
import type { SnapshotUploadFile } from "@/components/snapshot/useSnapshotUpload";
import { useSnapshotSection } from "@/components/snapshot/snapshotSection.context";
import { SnapshotDropzoneFileActions } from "./SnapshotDropzoneFileActions";
import { SnapshotDropzoneRow } from "./SnapshotDropzoneRow";
import { toReportUploadedFileFromItem, toSydneyDateTimeText } from "./snapshotDropzone.utils";

interface SnapshotDropzoneUploadingRowProps {
  item: SnapshotUploadFile;
  /** Its entry in the day's S3 list, once refetched after the upload. */
  listed: ReportUploadedFile | undefined;
}

/**
 * A file dropped here as one row for its whole life (uploading, then uploaded or failed), so it's
 * never swapped for another; once uploaded it acts like any file in S3.
 */
export const SnapshotDropzoneUploadingRow = (props: SnapshotDropzoneUploadingRowProps) => {
  const { item, listed } = props;
  const section = useSnapshotSection();
  const isFailed = item.status === "failed";
  const isUploaded = item.status === "uploaded" && item.key !== null;
  const percent = Math.round(item.progress * 100);

  const handleRetry = () => {
    section.retryFile(item.id);
  };

  const handleRemove = () => {
    section.removeFile(item.id);
  };

  let status: ReactNode = "Uploading";
  let actionKey = "progress";
  const progress: ReactNode = (
    <LinearProgress
      sx={{
        borderRadius: 4,
        height: 8,
        ml: 1,
        mt: 1.25,
      }}
      value={percent}
      variant="determinate"
    />
  );
  let action: ReactNode = (
    <Typography
      color="text.secondary"
      sx={{
        minWidth: 36,
        textAlign: "right",
      }}
      variant="body2"
    >
      {percent}%
    </Typography>
  );
  if (isUploaded) {
    status = `uploaded ${toSydneyDateTimeText(listed?.uploadedAt ?? item.uploadedAt ?? new Date().toISOString())}`;
    let uploaded: ReportUploadedFile | undefined;
    if (listed && item.key) uploaded = toReportUploadedFileFromItem(item, item.key, listed);
    actionKey = "actions";
    if (uploaded && section.deletion.file?.key === uploaded.key) actionKey = "confirm";
    action = <SnapshotDropzoneFileActions file={uploaded} />;
  }
  else if (isFailed) {
    actionKey = "failed";
    status = (
      <Box
        component="span"
        sx={{ color: "error.main" }}
      >
        Failed: {item.error}
      </Box>
    );
    action = (
      <Stack direction="row">
        <IconButton
          aria-label={`Retry ${item.file.name}`}
          onClick={handleRetry}
          size="small"
        >
          <ReplayIcon fontSize="small" />
        </IconButton>
        <IconButton
          aria-label={`Remove ${item.file.name}`}
          color="error"
          onClick={handleRemove}
          size="small"
        >
          <DeleteOutlinedIcon fontSize="small" />
        </IconButton>
      </Stack>
    );
  }

  return (
    <SnapshotDropzoneRow
      action={action}
      actionKey={actionKey}
      name={item.file.name}
      progress={progress}
      showProgress={item.status === "uploading"}
      size={listed?.size ?? item.file.size}
      status={status}
      title={item.key ?? item.file.name}
    />
  );
};
